import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { Logo } from "@/components/layout/Logo";
import { PrimaryButton, SecondaryButton, Eyebrow } from "@/components/fionova/Primitives";
import { generateReveal } from "@/services/unsaidService";
import { unsaidStore, transcriptHash } from "@/lib/unsaidStore";
import { trackSessionOnce } from "@/lib/analytics";

const FEEDBACK_OPTIONS = [
  { value: "yes", label: "Yes, very much" },
  { value: "some", label: "Some of it" },
  { value: "not_really", label: "Not really" },
];

export default function TheUnsaidReveal() {
  const navigate = useNavigate();
  const [transcript] = useState(() => unsaidStore.getTranscript());
  const userTurns = transcript.filter((m) => m.role === "user").length;

  const [status, setStatus] = useState("loading"); // loading | intro | reveal | error | missing
  const [reveal, setReveal] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const startedRef = useRef(false);

  const load = useCallback(async () => {
    if (userTurns < 1) { setStatus("missing"); return; }
    const hash = transcriptHash(transcript);
    const cached = unsaidStore.getRevealCache(hash);
    if (cached) { setReveal((p) => p || cached); setStatus((s) => (s === "reveal" ? s : "intro")); return; }
    setStatus((s) => (s === "reveal" ? s : "loading"));
    const result = await generateReveal(transcript);
    if (!result.ok) { setStatus((s) => (s === "reveal" ? s : "error")); return; }
    unsaidStore.setRevealCache(hash, result);
    setReveal((p) => p || result);
    setStatus((s) => (s === "reveal" ? s : "intro"));
  }, [transcript, userTurns]);

  // Guard against React StrictMode double-invoke: generate exactly once.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    load();
  }, [load]);

  const chooseFeedback = (value) => {
    setFeedback(value);
    unsaidStore.saveOutcome({
      feedback: value,
      observations: reveal?.observations || [],
      handoff: reveal?.handoff || {},
      transcript,
    });
    setTimeout(() => navigate("/emotional-xray"), 650);
    // GA4: The Unsaid stage completed (step 1).
    trackSessionOnce("step_unsaid", "onboarding_step_completed", { step_name: "unsaid", step_number: 1 });
  };

  // ---- Missing transcript (direct nav / cleared session) ----
  if (status === "missing") {
    return (
      <OnboardingLayout testId="page-unsaid-reveal" contentClassName="items-center justify-center py-10 text-center">
        <Logo size={64} showTagline={false} />
        <h1 className="mt-8 text-2xl font-semibold tracking-tight text-foreground">Let's start with a conversation.</h1>
        <p className="mt-3 max-w-sm text-base leading-relaxed text-muted-foreground">
          The Unsaid begins with a few honest questions. When you're ready, we can take that first step together.
        </p>
        <div className="mt-8 w-full max-w-[280px]">
          <PrimaryButton data-testid="reveal-begin-unsaid" onClick={() => navigate("/the-unsaid")}>Begin The Unsaid</PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  // ---- Generating ----
  if (status === "loading") {
    return (
      <OnboardingLayout testId="page-unsaid-reveal" contentClassName="items-center justify-center py-10 text-center">
        <div className="flex items-center gap-1.5" data-testid="reveal-loading">
          {[0, 1, 2].map((i) => (
            <span key={i} className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
        <p className="mt-6 text-base text-muted-foreground">Putting together what I noticed…</p>
      </OnboardingLayout>
    );
  }

  // ---- Graceful error + retry ----
  if (status === "error") {
    return (
      <OnboardingLayout testId="page-unsaid-reveal" contentClassName="items-center justify-center py-10 text-center">
        <Logo size={64} showTagline={false} />
        <h1 className="mt-8 text-2xl font-semibold tracking-tight text-foreground">Let me take another moment.</h1>
        <p className="mt-3 max-w-sm text-base leading-relaxed text-muted-foreground">
          I wasn't quite able to gather my thoughts. Give me a moment to try again.
        </p>
        <div className="mt-8 w-full max-w-[280px]">
          <PrimaryButton data-testid="reveal-retry" onClick={load}>Try again</PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  // ---- Intro ----
  if (status === "intro") {
    const modest =
      (reveal?.observations?.length ?? 0) <= 1 &&
      reveal?.observations?.[0]?.strength === "tentative";
    return (
      <OnboardingLayout testId="page-unsaid-reveal" contentClassName="justify-center py-10">
        <div className="flex flex-col items-center text-center">
          <Logo size={64} showTagline={false} />
          <Eyebrow className="mt-8">The Unsaid</Eyebrow>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-foreground">
            {modest ? "Thank you for sharing with me." : "You gave me a lot to work with."}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">I want to show you something.</p>
          <div className="mt-8 rounded-2xl bg-card p-6 text-left ring-1 ring-border">
            <p className="text-base leading-relaxed text-foreground/90">
              This isn't a diagnosis, and I'm not claiming to know what you're thinking. I'm showing
              you patterns I noticed in what you shared. You decide what fits.
            </p>
          </div>
        </div>
        <div className="mt-10">
          <PrimaryButton data-testid="reveal-show" onClick={() => setStatus("reveal")}>Show me</PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  // ---- Reveal ----
  const observations = reveal?.observations || [];
  return (
    <div data-testid="page-unsaid-reveal" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto w-full max-w-[560px] px-6 py-12 sm:px-8">
        <div className="text-center">
          <Eyebrow>The Unsaid</Eyebrow>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight text-foreground">What I noticed</h1>
        </div>

        <div className="mt-10 space-y-8">
          {observations.map((o, i) => (
            <motion.article
              key={i}
              data-testid={`reveal-observation-${i}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="overflow-hidden rounded-3xl bg-card ring-1 ring-border"
            >
              <div className="border-b border-border px-6 py-5">
                <p className="eyebrow text-[11px] text-muted-foreground">What you said</p>
                <p className="mt-2 text-base leading-relaxed text-foreground/90">{o.what_you_said}</p>
              </div>
              <div className="border-b border-border px-6 py-5">
                <p className="eyebrow text-[11px] text-muted-foreground">What I noticed</p>
                <p className="mt-2 text-base leading-relaxed text-foreground/90">{o.what_i_noticed}</p>
              </div>
              <div className="bg-primary/[0.06] px-6 py-6">
                <p className="eyebrow text-[11px] text-primary">The Unsaid</p>
                <p className="mt-2 text-lg font-medium leading-relaxed text-foreground">{o.the_unsaid}</p>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Feedback */}
        <div className="mt-12" data-testid="reveal-feedback">
          <p className="text-center text-lg font-medium text-foreground">How much of this fits?</p>
          <div className="mt-6 flex flex-col gap-3">
            {FEEDBACK_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                data-testid={`reveal-feedback-${opt.value}`}
                onClick={() => chooseFeedback(opt.value)}
                disabled={!!feedback}
                className={`rounded-full px-6 py-4 text-base font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  feedback === opt.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground ring-1 ring-border hover:ring-primary/40"
                } ${feedback && feedback !== opt.value ? "opacity-40" : ""}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
